import { jsx, jsxs, Fragment as Fragment$1 } from "react/jsx-runtime";
import * as React from "react";
import { Fragment, useState, Children, useLayoutEffect, useRef, useEffect } from "react";
import { IconButton, BUTTON_SELECTOR, SecondaryButton, DangerButton, PrimaryButton } from "@ardoq/button";
import { FORM_WRAPPER_SELECTOR, TextInput, Checkbox } from "@ardoq/forms";
import { DoqFace, DoqSize } from "@ardoq/doq";
import { radius, colors, spacing, timeDefaultMs, zIndex } from "@ardoq/design-tokens";
import styled, { css, keyframes } from "styled-components";
import { IconName, Icon, IconSize, ICON_SELECTOR, SpinnerIcon } from "@ardoq/icons";
import { FlexBox, Box, Stack } from "@ardoq/layout";
import { TextOverflow } from "@ardoq/popovers";
import { header2, semibold16, Text, text2, InlineText, Paragraph } from "@ardoq/typography";
import { ErrorNotification, WarningNotification } from "@ardoq/status-ui";
import { TABS_GROUP_HEADER_SELECTOR } from "@ardoq/tabs";
import { getIsMounted, pushStackPage } from "@ardoq/stack-page-manager";
import { useKeydown, useIsMounted, useEffectOnce } from "@ardoq/hooks";
import { noop } from "lodash";
import { isArdoqError } from "@ardoq/common-helpers";
import { createPortal } from "react-dom";
var ModalSize = /* @__PURE__ */ ((ModalSize2) => {
  ModalSize2[ModalSize2["XS"] = 1] = "XS";
  ModalSize2[ModalSize2["S"] = 2] = "S";
  ModalSize2[ModalSize2["M"] = 3] = "M";
  ModalSize2[ModalSize2["L"] = 4] = "L";
  ModalSize2[ModalSize2["COVER"] = 5] = "COVER";
  return ModalSize2;
})(ModalSize || {});
const getCustomHeightCss = (customHeight) => typeof customHeight === "string" ? `height: ${customHeight};` : "";
const DEFAULT_FIXED_HEIGHT = {
  [ModalSize.XS]: "min-height: 240px;",
  [ModalSize.S]: "height: 63vh;",
  [ModalSize.M]: "height: 71vh;",
  [ModalSize.L]: "height: 93vh;"
};
const containerSizeProvider = ({
  modalSize,
  fixedHeight = false
}) => {
  if (modalSize === ModalSize.XS) {
    return css`
      width: 340px;
      min-height: 240px;
      max-height: 43vh;
      ${fixedHeight === true ? DEFAULT_FIXED_HEIGHT[ModalSize.XS] : getCustomHeightCss(fixedHeight)}
    `;
  } else if (modalSize === ModalSize.S) {
    return css`
      width: 500px;
      min-height: 240px;
      max-height: 63vh;
      ${fixedHeight === true ? DEFAULT_FIXED_HEIGHT[ModalSize.S] : getCustomHeightCss(fixedHeight)}
    `;
  } else if (modalSize === ModalSize.M) {
    return css`
      width: 900px;
      min-height: 240px;
      max-height: 71vh;
      ${fixedHeight === true ? DEFAULT_FIXED_HEIGHT[ModalSize.M] : getCustomHeightCss(fixedHeight)}
    `;
  } else if (modalSize === ModalSize.L) {
    return css`
      min-height: 240px;
      max-height: 93vh;

      width: 93vh;
      min-width: 1000px;
      max-width: 1440px;
      ${fixedHeight === true ? DEFAULT_FIXED_HEIGHT[ModalSize.L] : getCustomHeightCss(fixedHeight)}
    `;
  } else if (modalSize === ModalSize.COVER) {
    return css`
      width: 100vw;
      height: 100vh;
    `;
  }
  return css``;
};
const ModalContainerStyle = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${radius.r8};
  overflow: hidden;
  ${({ $modalSize, $fixedHeight }) => containerSizeProvider({
  modalSize: $modalSize,
  fixedHeight: $fixedHeight
})};
`;
const ModalContainer$1 = ({
  children,
  modalSize = ModalSize.XS,
  fixedHeight = false
}) => /* @__PURE__ */ jsx(ModalContainerStyle, { $modalSize: modalSize, $fixedHeight: fixedHeight, children });
const MODAL_CONTAINER_SELECTOR = ModalContainerStyle.toString();
const MODAL_HEADER_TITLE_ID = "modal-header-title";
const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: ${colors.surfaceDefault};
  padding: ${spacing.s16} ${spacing.s24};
  border-bottom: 1px solid ${colors.borderSubtle00};
`;
const HeaderTitle = styled(TextOverflow)`
  ${header2};
  color: ${colors.textDefault};
`;
const ModalHeader = ({
  children,
  title,
  onCloseButtonClick,
  className,
  headerTitleClickId,
  closeButtonClickId,
  headerTitleDataTestId,
  closeButtonDataTestId,
  isCloseButtonDisabled
}) => /* @__PURE__ */ jsx(HeaderWrapper, { className, children: /* @__PURE__ */ jsxs(FlexBox, { align: "center", justify: "space-between", width: "full", padding: "none", children: [
  /* @__PURE__ */ jsxs(
    HeaderTitle,
    {
      id: MODAL_HEADER_TITLE_ID,
      tabIndex: 0,
      "data-click-id": headerTitleClickId,
      "data-test-id": headerTitleDataTestId,
      children: [
        title,
        children
      ]
    }
  ),
  onCloseButtonClick && /* @__PURE__ */ jsx(
    IconButton,
    {
      iconName: IconName.CLOSE,
      onClick: onCloseButtonClick,
      isDisabled: isCloseButtonDisabled,
      "aria-label": "Close",
      "data-click-id": closeButtonClickId,
      "data-test-id": closeButtonDataTestId
    }
  )
] }) });
const FooterContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  border-top: 1px solid ${colors.borderSubtle00};
  background-color: ${colors.surfaceDefault};
  gap: ${spacing.s8};
  padding: ${spacing.s16} ${spacing.s24};
  align-items: flex-end;
`;
const ModalFooter = ({ children }) => /* @__PURE__ */ jsx(FooterContainer, { children });
const ModalSubtitleStyle = styled.div`
  ${semibold16}
`;
const ModalSubtitle = ({ children }) => /* @__PURE__ */ jsx(ModalSubtitleStyle, { children });
const ModalText = ({ children }) => /* @__PURE__ */ jsx(Text, { children });
const ModalListItemStyle = styled.li`
  list-style: none;
  min-height: ${spacing.s24};
  display: flex;
  align-items: center;

  i:first-child,
  svg:first-child {
    margin-right: 8px;
    color: ${colors.icon};
    flex-shrink: 0;
  }
`;
const ModalListWrapperStyle = styled.ul`
  margin: 0;
  padding: 0;
  ${text2}

  ${ModalListItemStyle} {
    line-height: 20px;
    margin-bottom: ${spacing.s4};
    &:last-child {
      margin-bottom: 0;
    }
  }
`;
const ModalListItemStrong = ({ children }) => /* @__PURE__ */ jsx(InlineText, { variant: "text2Bold", children });
const ModalListItem = ({
  children,
  iconName
}) => /* @__PURE__ */ jsxs(ModalListItemStyle, { children: [
  /* @__PURE__ */ jsx(
    Icon,
    {
      iconName: iconName || IconName.ARROW_FORWARD,
      iconSize: IconSize.REGULAR
    }
  ),
  /* @__PURE__ */ jsx("div", { children })
] });
const ModalListWrapper = ({ children }) => /* @__PURE__ */ jsx(ModalListWrapperStyle, { children });
const ModalList = ({ options = [] }) => options.length ? /* @__PURE__ */ jsx(ModalListWrapper, { children: options.map((option, index) => /* @__PURE__ */ jsxs(ModalListItem, { iconName: option.iconName, children: [
  option.strongText && /* @__PURE__ */ jsx(ModalListItemStrong, { children: option.strongText }),
  " ",
  option.text
] }, index)) }) : null;
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spacing.s8};
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  ${({ $modalSize }) => ModalSize.XS === $modalSize && css`
      flex-direction: column;
      align-items: initial;
      ${BUTTON_SELECTOR} {
        display: flex;
        justify-content: center;
      }
    `};
`;
const DoqWrapper = styled.div`
  align-self: center;
`;
const ModalLayout = styled(Box).attrs({
  paddingX: "large",
  paddingTop: "medium"
})`
  display: flex;
  flex-direction: column;
  gap: ${spacing.s16};
  &:last-child {
    padding-bottom: ${spacing.s24};
  }
`;
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  ${ICON_SELECTOR} {
    color: ${colors.iconOnInvertedStrong};
  }
`;
const ModalLoader = () => /* @__PURE__ */ jsx(Wrapper, { children: /* @__PURE__ */ jsx(SpinnerIcon, { iconSize: 64 }) });
const ModalBodyStyle = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${spacing.s16};
  background: ${colors.surfaceDefault};
  overflow-y: auto;

  ${TABS_GROUP_HEADER_SELECTOR} {
    padding: 0 ${spacing.s16};
    border-bottom: 1px solid ${colors.borderSubtle00};
  }

  & > ${FORM_WRAPPER_SELECTOR} {
    max-width: 700px;
  }
`;
const ModalBody = ({ children }) => /* @__PURE__ */ jsx(ModalBodyStyle, { children });
const ModalBodyWrapper = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  background-color: ${colors.surfaceDefault};
`;
const ModalWrapper = ({ children }) => /* @__PURE__ */ jsx(ModalBodyWrapper, { children: /* @__PURE__ */ jsx(ModalBody, { children }) });
const DoqAndTextPositioner = ({
  shouldStack,
  children
}) => {
  return shouldStack ? /* @__PURE__ */ jsx(Fragment, { children }) : /* @__PURE__ */ jsx(FlexBox, { gap: "medium", children });
};
const ModalTemplate = ({
  modalSize,
  fixedHeight,
  onCloseButtonClick,
  headerText,
  subtitle,
  text,
  text2: text22,
  inputLabel,
  rememberActionCustomCheckboxText,
  showRememberActionCheckbox,
  onRememberActionCheckboxChange,
  rememberActionCheckboxState,
  errorBlockMessage,
  warningBlockMessage,
  listOptions = [],
  formContent,
  footerContent,
  children,
  secondaryButtonText,
  secondaryButtonClickId,
  secondaryButtonDataTestId,
  isSecondaryButtonDisabled,
  onSecondaryButtonClick,
  primaryButtonText,
  primaryButtonClickId,
  primaryButtonDataTestId,
  isPrimaryButtonDisabled,
  onPrimaryButtonClick,
  dangerButtonText,
  dangerButtonClickId,
  dangerButtonDataTestId,
  isDangerButtonDisabled,
  onDangerButtonClick,
  inputValue,
  onInputValueChange,
  doqType,
  onInputEnterPress,
  textInputId,
  inputPlaceHolderText,
  notification,
  isInProgress,
  isLoading,
  primaryButtonRef,
  textInputRef,
  inputDataTestId
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const shouldRenderHeader = headerText || onCloseButtonClick;
  const shouldRenderFooter = footerContent || showRememberActionCheckbox || secondaryButtonText || dangerButtonText || primaryButtonText;
  const shouldRenderTextSection = subtitle || text || text22 || Boolean(listOptions.length);
  const shouldRenderInputSection = inputLabel || onInputValueChange || formContent;
  const shouldRenderNotificationSection = errorBlockMessage || warningBlockMessage || notification;
  const shouldRenderMainContentSection = shouldRenderNotificationSection || shouldRenderTextSection || doqType || shouldRenderInputSection;
  const contentShouldStack = modalSize && modalSize === ModalSize.XS;
  if (isLoading) return /* @__PURE__ */ jsx(ModalLoader, {});
  const getAsyncSubmitHandler = (closeCallback) => async () => {
    if (!closeCallback) return;
    setIsSubmitting(true);
    await closeCallback();
  };
  return /* @__PURE__ */ jsxs(
    ModalContainer$1,
    {
      modalSize: modalSize || (doqType ? ModalSize.S : ModalSize.XS),
      fixedHeight,
      children: [
        shouldRenderHeader && /* @__PURE__ */ jsx(
          ModalHeader,
          {
            onCloseButtonClick,
            isCloseButtonDisabled: isInProgress ?? isSubmitting,
            children: headerText
          }
        ),
        /* @__PURE__ */ jsxs(ModalWrapper, { children: [
          shouldRenderMainContentSection && /* @__PURE__ */ jsxs(ModalLayout, { children: [
            shouldRenderNotificationSection && /* @__PURE__ */ jsxs(Fragment$1, { children: [
              notification,
              errorBlockMessage && /* @__PURE__ */ jsx(ErrorNotification, { children: errorBlockMessage }),
              warningBlockMessage && /* @__PURE__ */ jsx(WarningNotification, { children: warningBlockMessage })
            ] }),
            (doqType || shouldRenderTextSection) && /* @__PURE__ */ jsxs(DoqAndTextPositioner, { shouldStack: Boolean(contentShouldStack), children: [
              doqType && /* @__PURE__ */ jsx(DoqWrapper, { children: /* @__PURE__ */ jsx(DoqFace, { doqType, doqSize: DoqSize.SMALL }) }),
              shouldRenderTextSection && /* @__PURE__ */ jsx(Text, { variant: "text2", children: /* @__PURE__ */ jsxs(Stack, { gap: "medium", children: [
                subtitle && /* @__PURE__ */ jsx(ModalSubtitle, { children: subtitle }),
                Children.toArray(text).filter(Boolean).map((textParagraph, index) => /* @__PURE__ */ jsx(ModalText, { children: textParagraph }, index)),
                Boolean(listOptions.length) && /* @__PURE__ */ jsx(ModalList, { options: listOptions }),
                Children.toArray(text22).filter(Boolean).map((textParagraph, index) => /* @__PURE__ */ jsx(ModalText, { children: textParagraph }, index))
              ] }) })
            ] }),
            shouldRenderInputSection && /* @__PURE__ */ jsxs(Fragment$1, { children: [
              (inputLabel || onInputValueChange) && /* @__PURE__ */ jsx(
                TextInput,
                {
                  ref: textInputRef,
                  placeholder: inputPlaceHolderText,
                  id: textInputId,
                  label: inputLabel,
                  value: inputValue,
                  onValueChange: onInputValueChange,
                  isDisabled: isInProgress ?? isSubmitting,
                  onKeyDown: ({ key }) => key === "Enter" && getAsyncSubmitHandler(onInputEnterPress)(),
                  dataTestId: inputDataTestId
                }
              ),
              formContent
            ] })
          ] }),
          children,
          showRememberActionCheckbox && /* @__PURE__ */ jsx(ModalLayout, { children: /* @__PURE__ */ jsx(
            Checkbox,
            {
              isChecked: Boolean(rememberActionCheckboxState),
              onChange: () => onRememberActionCheckboxChange == null ? void 0 : onRememberActionCheckboxChange(!rememberActionCheckboxState),
              children: /* @__PURE__ */ jsx(Paragraph, { variant: "text2", children: rememberActionCustomCheckboxText || "Don't show this message again" })
            }
          ) })
        ] }),
        shouldRenderFooter && /* @__PURE__ */ jsxs(ModalFooter, { children: [
          (secondaryButtonText || dangerButtonText || primaryButtonText) && /* @__PURE__ */ jsxs(
            ButtonContainer,
            {
              $modalSize: modalSize || (doqType ? ModalSize.S : ModalSize.XS),
              children: [
                secondaryButtonText && /* @__PURE__ */ jsx(
                  SecondaryButton,
                  {
                    onClick: onSecondaryButtonClick,
                    isDisabled: isSecondaryButtonDisabled || (isInProgress ?? isSubmitting),
                    "data-click-id": secondaryButtonClickId,
                    dataTestId: secondaryButtonDataTestId,
                    children: secondaryButtonText
                  }
                ),
                dangerButtonText && /* @__PURE__ */ jsx(
                  DangerButton,
                  {
                    onClick: getAsyncSubmitHandler(onDangerButtonClick),
                    isDisabled: isDangerButtonDisabled,
                    isLoading: isInProgress ?? isSubmitting,
                    "data-click-id": dangerButtonClickId,
                    dataTestId: dangerButtonDataTestId,
                    children: dangerButtonText
                  }
                ),
                primaryButtonText && /* @__PURE__ */ jsx(
                  PrimaryButton,
                  {
                    onClick: getAsyncSubmitHandler(onPrimaryButtonClick),
                    isDisabled: isPrimaryButtonDisabled,
                    isLoading: isInProgress ?? isSubmitting,
                    "data-click-id": primaryButtonClickId,
                    dataTestId: primaryButtonDataTestId,
                    ref: primaryButtonRef,
                    children: primaryButtonText
                  }
                )
              ]
            }
          ),
          footerContent
        ] })
      ]
    }
  );
};
const fullScreenCSS = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const backdropCSS = css`
  ${fullScreenCSS};
  user-select: none;
  background-color: rgba(0, 0, 0, 0.2);
`;
const DarkBackdropContent = styled.div`
  ${backdropCSS};
`;
const FullscreenDiv = styled.div`
  ${fullScreenCSS};
  z-index: 1;
  ${({ $isDrawer }) => $isDrawer && "align-items:flex-end"};
`;
const ModalBackdrop = styled.div`
  ${backdropCSS};

  /* ANIMATION */
  transition: opacity ${timeDefaultMs};
  opacity: ${({ $isVisible }) => $isVisible ? 1 : 0};
`;
const ModalContentContainer = styled.div`
  z-index: 1;
  pointer-events: auto;
  &:focus {
    outline: none;
  }

  /* ANIMATION */
  transition:
    opacity ${timeDefaultMs},
    transform ${timeDefaultMs};
  opacity: ${({ $isVisible }) => $isVisible ? 1 : 0};
  transform: ${({ $isVisible }) => `translateY(${$isVisible ? 0 : "-50px"})}`};
`;
const useResetTransform = (ref) => {
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const removeTransform = () => {
      node.style.transform = "none";
    };
    node.addEventListener("transitionend", removeTransform, false);
    return () => {
      node.removeEventListener("transitionend", removeTransform);
    };
  }, [ref]);
};
const isInputElement = (element) => element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;
const focusModalContainer = (element) => {
  const target = element.closest(String(ModalContentContainer));
  if (!(target instanceof HTMLElement)) {
    return;
  }
  target.setAttribute("tabindex", "0");
  target.addEventListener("blur", () => target.removeAttribute("tabindex"), {
    once: true
  });
  target.focus();
  return;
};
const handleEscapeButtonPressed = (onEscPress) => ({ key }) => {
  if (key !== "Escape" || !onEscPress) {
    return;
  }
  const { activeElement } = document;
  if (isInputElement(activeElement)) {
    focusModalContainer(activeElement);
    return;
  }
  return onEscPress();
};
const ModalContainer = ({
  onBackdropClick,
  onEscPress = () => {
  },
  children
}) => {
  useKeydown(handleEscapeButtonPressed(onEscPress));
  const isVisible = useIsMounted();
  const modalContainerRef = React.useRef(null);
  useResetTransform(modalContainerRef);
  return /* @__PURE__ */ jsxs(FullscreenDiv, { children: [
    /* @__PURE__ */ jsx(ModalBackdrop, { onClick: onBackdropClick, $isVisible: isVisible }),
    /* @__PURE__ */ jsx(
      ModalContentContainer,
      {
        role: "modal",
        $isVisible: isVisible,
        ref: modalContainerRef,
        children
      }
    )
  ] });
};
const resolveBoolean = async (selector) => {
  if (typeof selector === "function") {
    return await selector();
  }
  return selector;
};
async function modal(content, {
  shouldCloseOnBackdropClick = true,
  shouldCloseOnEscapeKey = true,
  autoFocus = true,
  onClose
} = {}) {
  await getIsMounted();
  const result = new Promise((resolve) => {
    pushStackPage(
      ({ closeStackPage, stackPageIsInFocus }) => {
        const handleResolve = async (result2) => {
          await closeStackPage();
          if (onClose) onClose();
          resolve(result2);
        };
        return /* @__PURE__ */ jsx(
          ModalContainer,
          {
            onEscPress: async () => {
              if (!stackPageIsInFocus) {
                return;
              }
              return await resolveBoolean(shouldCloseOnEscapeKey) && handleResolve(false);
            },
            onBackdropClick: async () => await resolveBoolean(shouldCloseOnBackdropClick) && handleResolve(false),
            children: content(handleResolve)
          }
        );
      },
      {
        autoFocus
      }
    );
  });
  return result;
}
const AlertDialog = ({
  title,
  errorMessage,
  body,
  showRememberActionCheckbox,
  rememberActionCustomCheckboxText,
  confirmButtonTitle = "Close",
  confirmButtonClickId,
  isConfirmButtonDanger,
  onClose,
  ...restOfDialogBaseProps
}) => {
  const [rememberAction, setRememberAction] = useState(false);
  const onCloseProp = () => onClose(showRememberActionCheckbox ? { rememberAction } : false);
  return /* @__PURE__ */ jsx(
    ModalTemplate,
    {
      headerText: title,
      rememberActionCheckboxState: rememberAction,
      onRememberActionCheckboxChange: setRememberAction,
      rememberActionCustomCheckboxText,
      showRememberActionCheckbox,
      errorBlockMessage: errorMessage,
      primaryButtonText: !isConfirmButtonDanger ? confirmButtonTitle : "",
      primaryButtonClickId: confirmButtonClickId,
      onPrimaryButtonClick: onCloseProp,
      dangerButtonText: isConfirmButtonDanger ? confirmButtonTitle : "",
      dangerButtonClickId: confirmButtonClickId,
      onDangerButtonClick: onCloseProp,
      ...restOfDialogBaseProps,
      children: body
    }
  );
};
const alert = ({
  shouldCloseOnBackdropClick,
  shouldCloseOnEscapeKey,
  ...config
}) => {
  return modal(
    (resolve) => /* @__PURE__ */ jsx(AlertDialog, { onClose: (result) => resolve(result), ...config }),
    { shouldCloseOnBackdropClick, shouldCloseOnEscapeKey }
  );
};
const InfoDialog = ({
  body,
  onClose,
  title,
  errorMessage,
  onPrimaryButtonClick,
  ...restOfDialogBaseProps
}) => /* @__PURE__ */ jsx(
  ModalTemplate,
  {
    headerText: title,
    onCloseButtonClick: onClose,
    errorBlockMessage: errorMessage,
    onPrimaryButtonClick: () => {
      onPrimaryButtonClick == null ? void 0 : onPrimaryButtonClick();
      onClose();
    },
    ...restOfDialogBaseProps,
    children: body
  }
);
const info = ({
  shouldCloseOnBackdropClick,
  shouldCloseOnEscapeKey,
  ...config
}) => {
  return modal((resolve) => /* @__PURE__ */ jsx(InfoDialog, { onClose: resolve, ...config }), {
    shouldCloseOnBackdropClick,
    shouldCloseOnEscapeKey
  });
};
const handleOnConfirmAsync = ({
  onConfirmAsync,
  resolve
}) => {
  return async (result) => {
    try {
      const response = await (onConfirmAsync == null ? void 0 : onConfirmAsync(result));
      if (isArdoqError(response)) {
        resolve(false);
        alert({
          title: "Error occured",
          subtitle: "The action couldn't be completed"
        });
      }
      resolve(result);
    } catch (error) {
      resolve(false);
      alert({
        title: "Error occured",
        subtitle: "The action couldn't be completed"
      });
    }
  };
};
const ConfirmDialog = ({
  title,
  errorMessage,
  body,
  rememberActionCustomCheckboxText,
  showRememberActionCheckbox,
  confirmButtonTitle = "Ok",
  confirmButtonClickId,
  confirmButtonDataTestId,
  cancelButtonTitle = "Cancel",
  cancelButtonClickId,
  cancelButtonDataTestId,
  isConfirmButtonDanger,
  doqType,
  onConfirm,
  onCancel,
  ...restOfDialogBaseProps
}) => {
  const [rememberAction, setRememberAction] = useState(false);
  const onConfirmProp = () => onConfirm({
    ...showRememberActionCheckbox ? { rememberAction } : {},
    confirmed: true
  });
  return /* @__PURE__ */ jsx(
    ModalTemplate,
    {
      headerText: title,
      errorBlockMessage: errorMessage,
      rememberActionCheckboxState: rememberAction,
      onRememberActionCheckboxChange: setRememberAction,
      rememberActionCustomCheckboxText: rememberActionCustomCheckboxText || "Don’t ask me again",
      showRememberActionCheckbox,
      primaryButtonText: !isConfirmButtonDanger ? confirmButtonTitle : "",
      primaryButtonClickId: confirmButtonClickId,
      primaryButtonDataTestId: confirmButtonDataTestId,
      onPrimaryButtonClick: onConfirmProp,
      dangerButtonText: isConfirmButtonDanger ? confirmButtonTitle : "",
      dangerButtonClickId: confirmButtonClickId,
      dangerButtonDataTestId: confirmButtonDataTestId,
      onDangerButtonClick: onConfirmProp,
      secondaryButtonText: cancelButtonTitle,
      secondaryButtonClickId: cancelButtonClickId,
      secondaryButtonDataTestId: cancelButtonDataTestId,
      onSecondaryButtonClick: onCancel,
      doqType,
      ...restOfDialogBaseProps,
      children: body
    }
  );
};
const ConfirmDeleteDialog = ({
  title,
  errorMessage,
  body,
  rememberActionCustomCheckboxText,
  showRememberActionCheckbox,
  confirmButtonTitle = "Delete",
  confirmButtonClickId,
  confirmButtonDataTestId,
  cancelButtonTitle = "Cancel",
  modalSize = ModalSize.S,
  cancelButtonClickId,
  cancelButtonDataTestId,
  isConfirmButtonDanger = true,
  doqType,
  onConfirm,
  onCancel,
  ...restOfDialogBaseProps
}) => {
  const [rememberAction, setRememberAction] = useState(false);
  const onConfirmProp = () => onConfirm({
    ...showRememberActionCheckbox ? { rememberAction } : {},
    confirmed: true
  });
  return /* @__PURE__ */ jsx(
    ModalTemplate,
    {
      headerText: title,
      errorBlockMessage: errorMessage,
      rememberActionCheckboxState: rememberAction,
      onRememberActionCheckboxChange: setRememberAction,
      rememberActionCustomCheckboxText: rememberActionCustomCheckboxText || "Don’t ask me again",
      showRememberActionCheckbox,
      primaryButtonClickId: confirmButtonClickId,
      primaryButtonDataTestId: confirmButtonDataTestId,
      onPrimaryButtonClick: onConfirmProp,
      dangerButtonText: isConfirmButtonDanger ? confirmButtonTitle : "",
      dangerButtonClickId: confirmButtonClickId,
      dangerButtonDataTestId: confirmButtonDataTestId,
      onDangerButtonClick: onConfirmProp,
      secondaryButtonText: cancelButtonTitle,
      secondaryButtonClickId: cancelButtonClickId,
      secondaryButtonDataTestId: cancelButtonDataTestId,
      onSecondaryButtonClick: onCancel,
      doqType,
      modalSize,
      ...restOfDialogBaseProps,
      children: body
    }
  );
};
const confirm = ({
  onConfirmAsync = noop,
  shouldCloseOnBackdropClick,
  shouldCloseOnEscapeKey,
  ...config
}) => {
  return modal(
    (resolve) => /* @__PURE__ */ jsx(
      ConfirmDialog,
      {
        ...config,
        onConfirm: handleOnConfirmAsync({ onConfirmAsync, resolve }),
        onCancel: () => resolve(false)
      }
    ),
    { shouldCloseOnBackdropClick, shouldCloseOnEscapeKey }
  );
};
const confirmDelete = ({
  isConfirmButtonDanger = true,
  confirmButtonTitle = "Delete",
  onConfirmAsync = noop,
  shouldCloseOnBackdropClick,
  shouldCloseOnEscapeKey,
  ...config
}) => {
  return modal(
    (resolve) => /* @__PURE__ */ jsx(
      ConfirmDialog,
      {
        isConfirmButtonDanger,
        confirmButtonTitle,
        onConfirm: handleOnConfirmAsync({ onConfirmAsync, resolve }),
        onCancel: () => resolve(false),
        ...config
      }
    ),
    { shouldCloseOnBackdropClick, shouldCloseOnEscapeKey }
  );
};
const ConfirmWithYesInputDialog = ({
  title,
  errorMessage,
  body,
  rememberActionCustomCheckboxText,
  showRememberActionCheckbox,
  confirmButtonTitle = "Ok",
  confirmButtonClickId,
  cancelButtonTitle = "Cancel",
  cancelButtonClickId,
  isConfirmButtonDanger,
  modalSize = ModalSize.S,
  onConfirm,
  onCancel,
  inputDataTestId,
  ...restOfDialogBaseProps
}) => {
  const inputRef = useRef(null);
  const [rememberAction, setRememberAction] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const isConfirmButtonDisabled = inputValue.toLowerCase() !== "yes";
  const onConfirmProp = isConfirmButtonDisabled ? void 0 : () => onConfirm({
    ...showRememberActionCheckbox ? { rememberAction } : {},
    confirmed: true
  });
  useEffectOnce(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  });
  return /* @__PURE__ */ jsx(
    ModalTemplate,
    {
      modalSize,
      headerText: title,
      inputLabel: 'Type "yes" to continue',
      onInputValueChange: setInputValue,
      inputValue,
      onInputEnterPress: onConfirmProp,
      textInputRef: inputRef,
      errorBlockMessage: errorMessage,
      rememberActionCheckboxState: rememberAction,
      onRememberActionCheckboxChange: setRememberAction,
      rememberActionCustomCheckboxText: rememberActionCustomCheckboxText || "Don’t ask me again",
      showRememberActionCheckbox,
      primaryButtonText: !isConfirmButtonDanger ? confirmButtonTitle : "",
      primaryButtonClickId: confirmButtonClickId,
      onPrimaryButtonClick: onConfirmProp,
      isPrimaryButtonDisabled: isConfirmButtonDisabled,
      dangerButtonText: isConfirmButtonDanger ? confirmButtonTitle : "",
      dangerButtonClickId: confirmButtonClickId,
      onDangerButtonClick: onConfirmProp,
      isDangerButtonDisabled: isConfirmButtonDisabled,
      secondaryButtonText: cancelButtonTitle,
      secondaryButtonClickId: cancelButtonClickId,
      onSecondaryButtonClick: onCancel,
      inputDataTestId,
      ...restOfDialogBaseProps,
      children: body
    }
  );
};
const confirmWithYesInput = ({
  shouldCloseOnBackdropClick,
  shouldCloseOnEscapeKey,
  onConfirmAsync,
  ...config
}) => {
  return modal(
    (resolve) => /* @__PURE__ */ jsx(
      ConfirmWithYesInputDialog,
      {
        ...config,
        onConfirm: handleOnConfirmAsync({ onConfirmAsync, resolve }),
        onCancel: () => resolve(false)
      }
    ),
    { shouldCloseOnBackdropClick, shouldCloseOnEscapeKey }
  );
};
const ConfirmDeleteWithYesInputDialog = ({
  isConfirmButtonDanger = true,
  confirmButtonTitle = "Delete",
  inputDataTestId = "confirm-delete-with-yes-input",
  ...config
}) => /* @__PURE__ */ jsx(
  ConfirmWithYesInputDialog,
  {
    ...config,
    isConfirmButtonDanger,
    confirmButtonTitle,
    inputDataTestId
  }
);
const confirmDeleteWithYesInput = ({
  shouldCloseOnBackdropClick,
  shouldCloseOnEscapeKey,
  onConfirmAsync,
  ...config
}) => {
  return modal(
    (resolve) => /* @__PURE__ */ jsx(
      ConfirmDeleteWithYesInputDialog,
      {
        ...config,
        onConfirm: handleOnConfirmAsync({ onConfirmAsync, resolve }),
        onCancel: () => resolve(false)
      }
    ),
    { shouldCloseOnBackdropClick, shouldCloseOnEscapeKey }
  );
};
const prompt = ({
  title,
  errorMessage,
  body,
  confirmButtonTitle = "Ok",
  confirmButtonClickId,
  cancelButtonTitle = "Cancel",
  cancelButtonClickId,
  isConfirmButtonDanger,
  initialInputValue,
  shouldCloseOnBackdropClick,
  shouldCloseOnEscapeKey,
  ...restOfDialogBaseProps
}) => {
  return modal(
    (resolve) => {
      const [inputValue, setInputValue] = useState(
        initialInputValue || ""
      );
      const onConfirm = inputValue ? () => handleOnConfirmAsync({ resolve })(inputValue) : void 0;
      return /* @__PURE__ */ jsx(
        ModalTemplate,
        {
          headerText: title,
          inputValue,
          onInputValueChange: setInputValue,
          errorBlockMessage: errorMessage,
          primaryButtonText: !isConfirmButtonDanger ? confirmButtonTitle : "",
          primaryButtonClickId: confirmButtonClickId,
          onPrimaryButtonClick: onConfirm,
          isPrimaryButtonDisabled: !inputValue,
          dangerButtonText: isConfirmButtonDanger ? confirmButtonTitle : "",
          dangerButtonClickId: confirmButtonClickId,
          onDangerButtonClick: onConfirm,
          onInputEnterPress: onConfirm,
          isDangerButtonDisabled: !inputValue,
          secondaryButtonText: cancelButtonTitle,
          secondaryButtonClickId: cancelButtonClickId,
          onSecondaryButtonClick: () => resolve(false),
          ...restOfDialogBaseProps,
          children: body
        }
      );
    },
    { shouldCloseOnBackdropClick, shouldCloseOnEscapeKey }
  );
};
const getSyncConflictModalTextProps = () => {
  return {
    headerText: "Technical issue",
    text: `
There are changes made by other users right now.
We are sorry that your changes cannot be saved and will be discarded.`,
    text2: "Please reload the page.",
    secondaryButtonText: "Cancel",
    primaryButtonText: "Reload"
  };
};
const getSyncConflictModalTextPropsWithBlame = (user) => {
  return {
    headerText: "Technical issue",
    text: /* @__PURE__ */ jsxs(Fragment$1, { children: [
      "This resource is being edited by",
      /* @__PURE__ */ jsx("strong", { children: ` "${user.name}" (${user.email}) ` }),
      "right now. We are sorry that your changes cannot be saved and will be discarded."
    ] }),
    text2: "Please reload the page.",
    secondaryButtonText: "Cancel",
    primaryButtonText: "Reload"
  };
};
const openSyncConflictModal = async (props) => {
  return modal((resolve) => /* @__PURE__ */ jsx(
    ModalTemplate,
    {
      ...props,
      onSecondaryButtonClick: () => {
        var _a;
        return (_a = props.onSecondaryButtonClick) == null ? void 0 : _a.call(props, resolve);
      },
      onPrimaryButtonClick: () => {
        var _a;
        return (_a = props.onPrimaryButtonClick) == null ? void 0 : _a.call(props, resolve);
      }
    }
  ));
};
const syncConflictAlert = async ({
  entityType,
  entityId,
  trackEvent
}) => {
  trackEvent({ entityId, entityType });
  const textProps = getSyncConflictModalTextProps();
  await openSyncConflictModal({
    ...textProps,
    onSecondaryButtonClick: (resolve) => resolve(false),
    onPrimaryButtonClick: () => location.reload()
  });
};
const Tab = () => null;
const generateTabsComponent = ({
  TabsContainer,
  TabButton
}) => (props) => {
  const children = [props.children].flat().filter((child) => Boolean(child));
  const [selected, setSelected] = useState(
    children.find(({ props: props2 }) => props2.isDefault) ?? children[0]
  );
  return /* @__PURE__ */ jsxs(Fragment$1, { children: [
    /* @__PURE__ */ jsx(TabsContainer, { children: children.map((child, index) => /* @__PURE__ */ jsx(
      TabButton,
      {
        isDisabled: child.props.isDisabled,
        isActive: selected === child,
        onClick: () => !child.props.isDisabled && setSelected(child),
        children: child.props.title
      },
      index
    )) }),
    selected == null ? void 0 : selected.props.children
  ] });
};
const ModalInlineIconStyle = styled(Icon)`
  display: inline;
  vertical-align: middle;
`;
const ModalInlineIcon = ({ iconName }) => /* @__PURE__ */ jsx(ModalInlineIconStyle, { iconName, iconSize: IconSize.SMALL });
const useTransitionState = (isOpen) => {
  const [state, setState] = useState("exited");
  const currentState = getCurrentState(state, isOpen);
  const updateState = () => {
    setState(getCurrentState(currentState, isOpen));
  };
  return [currentState, updateState];
};
const getCurrentState = (state, open) => {
  if (open && state === "exited") {
    return "mounting";
  }
  if (open && state === "mounting") {
    return "entering";
  }
  if (!open && state === "entering") {
    return "exiting";
  }
  if (!open && state === "exiting") {
    return "exited";
  }
  return state;
};
const AnimatedModalDialog = (props) => {
  const [backdropState, updateBackdropState] = useTransitionState(props.isOpen);
  const [dialogState, updateDialogState] = useTransitionState(props.isOpen);
  if (!props.isOpen && backdropState === "exited" && dialogState === "exited") {
    return null;
  }
  return /* @__PURE__ */ jsxs(Portal, { children: [
    /* @__PURE__ */ jsx(Overlay, { onClick: props.onClickOutside }),
    /* @__PURE__ */ jsx(
      AnimatedBackdrop,
      {
        $state: backdropState,
        onAnimationEnd: updateBackdropState,
        onTransitionEnd: updateBackdropState
      }
    ),
    /* @__PURE__ */ jsx(CenteredDialog, { children: /* @__PURE__ */ jsx(
      AnimatedDialog,
      {
        $state: dialogState,
        onAnimationEnd: updateDialogState,
        onTransitionEnd: updateDialogState,
        children: props.children
      }
    ) })
  ] });
};
const Overlay = styled.div`
  position: fixed;
  inset: 0;
`;
const CenteredDialog = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;
let portalChildrenCount = 0;
const Portal = ({ children }) => {
  const targetRef = useRef(getPortalTarget());
  useEffect(() => {
    const target = targetRef.current;
    portalChildrenCount++;
    document.body.appendChild(target);
    return () => {
      portalChildrenCount--;
      if (portalChildrenCount === 0) {
        target.remove();
      }
    };
  }, []);
  return createPortal(children, targetRef.current);
};
const tagName = "modal-container";
const getPortalTarget = () => {
  const existingTarget = document.querySelector(tagName);
  if (existingTarget) {
    return existingTarget;
  }
  const target = document.createElement(tagName);
  target.style.position = "absolute";
  target.style.zIndex = zIndex.MODAL_BACKDROP.toString();
  return target;
};
const transitionDuration = 0.2;
const transitionDelay = 0.05;
const emptyKeyframes = keyframes`
    from {
      opacity: 0;
    }
    to {
      opacity: 0;
    }
`;
const mountingDialogStyles = css`
  animation: ${emptyKeyframes} 0s;
`;
const enteringDialogStyles = css`
  opacity: 1;
  transition-delay: ${transitionDelay}s;
  transform: translateY(0);
`;
const exitingDialogStyles = css`
  transform: translateY(-10px);
`;
const AnimatedDialog = styled.div.attrs({
  role: "dialog",
  "aria-modal": "true",
  "aria-labelledby": MODAL_HEADER_TITLE_ID
})`
  opacity: 0;
  transition-duration: ${transitionDuration}s;
  transform: translateY(10px);
  pointer-events: auto;
  transition-property: opacity, transform;
  transition-delay: 0s;

  ${({ $state }) => $state === "mounting" && mountingDialogStyles}
  ${({ $state }) => $state === "entering" && enteringDialogStyles}
  ${({ $state }) => $state === "exiting" && exitingDialogStyles}
`;
const mountingBackdropStyles = css`
  animation: ${emptyKeyframes} 0s;
`;
const enteringBackdropStyles = css`
  opacity: 1;
  transition-delay: 0s;
`;
const AnimatedBackdrop = styled.div`
  opacity: 0;
  transition-duration: ${transitionDuration}s;
  position: fixed;
  inset: 0;
  transition-property: opacity;
  pointer-events: none;
  background-color: ${colors.bgOverlay};
  transition-delay: ${transitionDelay}s;
  ${({ $state }) => $state === "mounting" && mountingBackdropStyles}
  ${({ $state }) => $state === "entering" && enteringBackdropStyles}
`;
export {
  AlertDialog,
  AnimatedModalDialog,
  ConfirmDeleteDialog,
  ConfirmDeleteWithYesInputDialog,
  ConfirmDialog,
  ConfirmWithYesInputDialog,
  DarkBackdropContent,
  FullscreenDiv,
  InfoDialog,
  MODAL_CONTAINER_SELECTOR,
  ModalBody,
  ModalContainer$1 as ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalInlineIcon,
  ModalLayout,
  ModalList,
  ModalListItem,
  ModalListItemStrong,
  ModalListWrapper,
  ModalLoader,
  ModalSize,
  ModalSubtitle,
  ModalTemplate,
  ModalText,
  ModalContainer as StackModalContainer,
  Tab,
  alert,
  confirm,
  confirmDelete,
  confirmDeleteWithYesInput,
  confirmWithYesInput,
  generateTabsComponent,
  getSyncConflictModalTextProps,
  getSyncConflictModalTextPropsWithBlame,
  handleEscapeButtonPressed,
  info,
  modal,
  openSyncConflictModal,
  prompt,
  resolveBoolean,
  syncConflictAlert
};
//# sourceMappingURL=index.js.map
